import type { ExtractedLayer, SoilExtraction } from "@tasduvar/shared";

/**
 * Zemin etüt raporu — kural tabanlı yerel okuyucu (yapay zekâ yok). PDF metin satırlarından Türkçe geoteknik terimleri ve
 * birimleri tanır: γ, φ, c / c_u, emniyetli taşıma gücü, zemin sınıfı (TBDY 2018), SS/S1/SDS/SD1/PGA, yeraltı suyu, temel
 * derinliği, il/ilçe/ada/parsel/pafta ve tabaka tabloları. Her değer sayfa, kaynak cümle ve güven taşır; belirsiz (düşük güven)
 * değerler doldurulmaz, yalnız ipucu olarak gösterilir. Birimler: γ kN/m³, gerilme kPa (= kN/m²), açı °, derinlik m, ivme g.
 */

export type Conf = "high" | "medium" | "low";
export interface Found<T = number> { value: T; page: number; snippet: string; confidence: Conf; note?: string; design?: boolean; backfill?: boolean }
export type ParamKey = "gamma" | "gammaSat" | "gammaDry" | "phi" | "c" | "cu" | "qa" | "qult" | "mu" | "delta" | "df" | "SS" | "S1" | "SDS" | "SD1" | "PGA";
export interface LocalLayer { /** tablodaki katman no (ayrı tablolardaki aynı katmanı birleştirmek için) */ no?: number | null; name: string | null; depthTop: number | null; depthBottom: number | null; gamma: number | null; gammaSat: number | null; phi: number | null; c: number | null; cu: number | null; sptN: number | null; page: number; snippet: string }
export interface LocalReport {
  pageCount: number; scanned: boolean; title: string | null; date: string | null;
  site: { city?: Found<string>; district?: Found<string>; block?: Found<string>; parcel?: Found<string>; sheet?: Found<string> };
  siteClass?: Found<string>;
  /** value null = yeraltı suyuna rastlanmadı */
  groundwater?: Found<number | null>;
  /** Anahtar başına çözülmüş değer (düşük güvenli olabilir → doldurulmaz) */
  values: Partial<Record<ParamKey, Found>>;
  /** Dolgu (geri dolgu malzemesi) için raporun önerdiği değerler */
  backfill: Partial<Record<"gamma" | "phi" | "c", Found>>;
  candidates: Partial<Record<ParamKey, Found[]>>;
  layers: LocalLayer[];
  warnings: string[];
  /** Bina etüdü (radye, BKS/BYS, yapı ağırlığı…; istinat duvarı geçmiyor): Df ve taşıma gücü bina temeli içindir → uyarı */
  building?: { page: number; snippet: string };
}
export const BUILDING_WARN = "Raporda bina temeli için verilmiş — istinat duvarı için kontrol edin";
export interface PageLines { page: number; lines: string[] }

// ---------------------------------------------------------------------------------------------------------------------
// Metin normalleştirme: harf harf (uzunluk korunur → konumlar orijinal metinle hizalı): küçük harf, Türkçe/Yunan eşleme
const MAP: Record<string, string> = {
  İ: "i", I: "i", ı: "i", Ş: "s", ş: "s", Ğ: "g", ğ: "g", Ü: "u", ü: "u", Ö: "o", ö: "o", Ç: "c", ç: "c", Â: "a", â: "a", Î: "i", î: "i", Û: "u", û: "u",
  Φ: "φ", ϕ: "φ", ɸ: "φ", Ø: "φ", ø: "φ", "∅": "φ", "⌀": "φ", Γ: "γ", Σ: "σ", ς: "σ", "–": "-", "—": "-", "−": "-", "‐": "-", "‑": "-", "º": "°", "˚": "°",
  "’": "'", "‘": "'", "′": "'", "＝": "=", "：": ":", " ": " ", "\t": " ", µ: "μ",
};
export function norm(s: string): string {
  let out = "";
  for (const ch of s) {
    const m = MAP[ch];
    if (m !== undefined) { out += m; continue; }
    const l = ch.toLowerCase();
    out += l.length === ch.length ? l : ch;
  }
  // Birim üstleri: kN/m3 → kN/m³, kg/cm2 → kg/cm² (uzunluk aynı); "m 3" (ayrı parça) → "m³ "
  return out.replace(/(\/ ?c?m)([23])(?![\d.,])/g, (_, a: string, d: string) => a + (d === "3" ? "³" : "²"))
    .replace(/(\/ ?c?m) ([23])(?![\d.,])/g, (_, a: string, d: string) => a + (d === "3" ? "³" : "²") + " ");
}

const NUM = String.raw`\d+(?:[.,]\d+)?`;
const toNum = (s: string): number => Number(s.replace(",", "."));
const L = String.raw`[\p{L}]`;

type Kind = "gamma" | "angle" | "stress" | "g" | "depth" | "ratio";
interface Rule { key: ParamKey; label: RegExp; kind: Kind; min: number; max: number }
const rx = (s: string): RegExp => new RegExp(s, "gu");

// Etiketler (normalleştirilmiş metinde; aksansız). Sıra önemli değil: her satırda tüm eşleşmeler konuma göre dizilir.
const RULES: Rule[] = [
  { key: "gammaSat", kind: "gamma", min: 12, max: 26, label: rx(String.raw`doygun\s*birim\s*(?:hacim\s*)?agirli[gk]${L}*|(?<!${L})γ\s*_?\s*(?:sat|doy${L}*)(?!${L})`) },
  { key: "gammaDry", kind: "gamma", min: 10, max: 24, label: rx(String.raw`kuru\s*birim\s*(?:hacim\s*)?agirli[gk]${L}*|(?<!${L})γ\s*_?\s*(?:k|d|kuru|dry)(?!${L})`) },
  // γ tek başına: direnç katsayıları (γRv, γRh, γRp), su (γsu, γw) ve γ' hariç
  { key: "gamma", kind: "gamma", min: 12, max: 26, label: rx(String.raw`(?<!doygun\s{0,3}|kuru\s{0,3})(?:dogal\s*|tabii\s*|islak\s*|yas\s*)?birim\s*(?:hacim\s*)?agirli[gk]${L}*|(?<!${L})γ\s*_?\s*(?:n|dogal|tabii)(?!${L})|(?<!${L})γ(?![\p{L}_'])(?!\s*_?\s*(?:r\s*[vhp]|su|w)(?!${L}))|(?<!${L})gamma(?!${L})`) },
  { key: "phi", kind: "angle", min: 0, max: 50, label: rx(String.raw`icsel\s*surtunme\s*acis${L}*|kayma\s*(?:direnci|direnc|mukavemeti|dayanimi)\s*acis${L}*|(?<!(?:taban|temel|duvar|beton|zemin\s*-\s*beton)\s{0,3}(?:-\s*zemin\s*)?)surtunme\s*acis${L}*|φ\s*'?(?![\p{L}])|(?<!${L})(?:fi|phi)(?!${L})`) },
  { key: "cu", kind: "stress", min: 0, max: 1000, label: rx(String.raw`drenajsiz\s*(?:kayma\s*)?(?:dayanimi|mukavemeti|direnci|kohezyon${L}*)|(?<![\p{L}\d])c\s*_?\s*u(?![\p{L}\d])`) },
  { key: "c", kind: "stress", min: 0, max: 1000, label: rx(String.raw`(?<!drenajsiz\s{0,3})kohezyon${L}*|(?<![\p{L}\d])c\s*'?(?=\s*[=:(\[])`) },
  { key: "qa", kind: "stress", min: 20, max: 3000, label: rx(String.raw`(?:net\s*)?(?:emniyetli|emin|izin\s*verilebilir|musaade\s*edilebilir|guvenli)\s*(?:zemin\s*)?tasima\s*(?:gucu|kapasitesi|gerilmesi)|(?:zemin\s*)?emniyet\s*gerilmesi|tasarim\s*tasima\s*(?:gucu|kapasitesi|dayanimi)|(?<!${L})q\s*_?\s*(?:a|em|emn|all|net|t)(?![\p{L}\d])|σ\s*_?\s*(?:em|z|zem|all)(?![\p{L}\d])`) },
  { key: "qult", kind: "stress", min: 50, max: 20000, label: rx(String.raw`(?:nihai|limit|sinir)\s*tasima\s*(?:gucu|kapasitesi)|(?<!${L})q\s*_?\s*(?:ult|nihai|f)(?![\p{L}\d])`) },
  { key: "mu", kind: "ratio", min: 0.1, max: 1, label: rx(String.raw`(?:taban\s*)?surtunme\s*katsayis${L}*|(?<!${L})(?:μ|tan\s*δ)(?![\p{L}])(?!\s*\(?\s*poisson)`) },
  { key: "delta", kind: "angle", min: 0, max: 45, label: rx(String.raw`(?:taban|temel)\s*(?:-\s*|ile\s*)?(?:zemin\s*)?(?:arasi\s*|arasindaki\s*)?surtunme\s*acis${L}*|(?<!${L})δ\s*_?\s*b(?!${L})`) },
  { key: "df", kind: "depth", min: 0.2, max: 10, label: rx(String.raw`temel\s*(?:oturma\s*|taban\s*|gomulme\s*|teme[l]?\s*)?derinlig${L}*|(?<!${L})d\s*_?\s*f(?![\p{L}\d])`) },
  { key: "SDS", kind: "g", min: 0.02, max: 3, label: rx(String.raw`(?<![\p{L}\d])s\s?_?d\s?_?s(?![\p{L}\d])|kisa\s*periyot\s*tasarim\s*spektral\s*ivme\s*katsayis${L}*`) },
  { key: "SD1", kind: "g", min: 0.01, max: 1.5, label: rx(String.raw`(?<![\p{L}\d])s\s?_?d\s?_?1(?![\p{L}\d.,])|1(?:[.,]0)?\s*(?:saniye|sn|s)\s*periyo${L}*\s*(?:icin\s*)?tasarim\s*spektral\s*ivme\s*katsayis${L}*`) },
  { key: "SS", kind: "g", min: 0.02, max: 3, label: rx(String.raw`(?<![\p{L}\d])s\s?_?s(?![\p{L}\d])|kisa\s*periyot\s*harita\s*spektral\s*ivme\s*katsayis${L}*`) },
  { key: "S1", kind: "g", min: 0.01, max: 1.5, label: rx(String.raw`(?<![\p{L}\d])s\s?_?1(?![\p{L}\d.,])|1(?:[.,]0)?\s*(?:saniye|sn|s)\s*periyo${L}*\s*(?:icin\s*)?harita\s*spektral\s*ivme\s*katsayis${L}*`) },
  { key: "PGA", kind: "g", min: 0.01, max: 1.5, label: rx(String.raw`(?<!${L})pga(?!${L})|(?:en\s*buyuk|pik|maksimum)\s*(?:yer|zemin)\s*ivmesi`) },
];

/** Tasarım/öneri cümlesi: raporun hesapta kullanılmasını önerdiği değer (tabaka tablosundaki ham değerlere göre öncelikli) */
const DESIGN = /tasarim|hesap(?:lar)?da|onerilen|onerilmekte|onerilir|alinmasi|alinabilir|kullanilmasi|kullanilabilir|dikkate\s*alin|secilmis|secilen|alinmistir|kullanilmistir|kabul\s*edil/;
/** Geri dolgu (duvar arkası) bağlamı */
const BACKFILL = /(?:geri|arka)\s*dolgu|dolgu\s*malzeme|duvar\s*arka\w*\s*(?:dolgu|zemin)|granuler\s*dolgu|drenaj\s*dolgu|dolgu\s*(?:icin|olarak)\s*(?:kullanilacak|onerilen)/;
/** Deprem düzeyi: DD-2 (%10 / 50 yıl, 475 yıl) tercih edilir */
const DD2 = /dd\s*-?\s*2(?!\d)|475\s*yil|%\s*10\b|\b10\s*%/;
const DD_OTHER = /dd\s*-?\s*[134](?!\d)|2475|72\s*yil|43\s*yil|%\s*(?:2|50|68)\b/;
const GW_LABEL = /yer\s*alti\s*su|yeralti\s*su|(?<!\p{L})yass(?!\p{L})|(?<!\p{L})yas\s*s(?!\p{L})|(?:statik\s*)?su\s*seviyes|su\s*tablas/u;
const GW_NONE = /rastlan(?:ma|il)ma(?:mis|di|maktadir)|rastlanmadi|rastlanmamis|rastlanmadig|gozlenme(?:mis|di|mekte|dig)|karsilasilma(?:mis|di|dig)|tespit\s*edilme(?:mis|di|dig)|bulunma(?:mis|di|maktadir|dig)|olcul(?:e)?me(?:mis|di)|yoktur|mevcut\s*(?:degil|ol(?:madig|mayip))|gorulme(?:mis|di|dig)/;
/** Yönetmelik koşul cümleleri (ör. "DTS=1 … yeraltı suyu düzeyi temel tabanından 10 m'den yukarıda ise") ölçüm değildir */
const GW_RULE = /(?<!\p{L})dts(?!\p{L})|bolgelerde|durumunda|halinde|daha\s*(?:yukarida|sig|derin)|derinlikten\s*daha|(?<!\p{L})ise(?!\p{L})/u;
const UNIT = /^\s*(kn\/m³|kn\/m²|kpa|mpa|t\/m³|t\/m²|ton\/m³|ton\/m²|tf\/m³|tf\/m²|gr\/cm³|g\/cm³|kg\/cm²|kgf\/cm²|°|derece|m(?![\p{L}²³])|g(?![\p{L}]))/u;
const UNIT_ANY = /(kn\/m³|kn\/m²|kpa|mpa|t\/m³|t\/m²|ton\/m³|ton\/m²|tf\/m³|tf\/m²|gr\/cm³|g\/cm³|kg\/cm²|kgf\/cm²)/;

interface Conv { value: number; conf: Conf; note?: string }
/** Birim dönüşümü + makullük; birimsiz değer büyüklüğüne göre yorumlanır (orta güven) */
function convert(kind: Kind, v: number, unit: string | null, r: { min: number; max: number }): Conv | null {
  let out = v, conf: Conf = "high", note: string | undefined;
  const u = unit ?? "";
  if (kind === "gamma") {
    if (/^(?:t|ton|tf)\/m³$|^g(?:r)?\/cm³$/.test(u)) { out = v * 9.81; note = `${fmtN(v)} ${u} × 9,81`; }
    else if (u === "kn/m³") { /* aynen */ }
    else if (v > 1 && v < 2.8) { out = v * 9.81; conf = "medium"; note = `birim yok; ${fmtN(v)} t/m³ kabul edildi`; }
    else conf = "medium";
  } else if (kind === "stress") {
    if (/^(?:t|ton|tf)\/m²$/.test(u)) { out = v * 9.81; note = `${fmtN(v)} t/m² × 9,81`; }
    else if (/^kgf?\/cm²$/.test(u)) { out = v * 98.07; note = `${fmtN(v)} kg/cm² × 98,07`; }
    else if (u === "mpa") { out = v * 1000; note = `${fmtN(v)} MPa`; }
    else if (u === "kpa" || u === "kn/m²") { /* aynen */ }
    else if (r.min >= 20 && v < 10) { out = v * 98.07; conf = "low"; note = `birim yok; ${fmtN(v)} kg/cm² olabilir`; }
    else conf = "medium";
  } else if (kind === "angle") {
    if (u && u !== "°" && u !== "derece") conf = "medium";
    if (!u) conf = "medium";
  } else if (kind === "depth") {
    if (u && u !== "m") return null;
  } else if (kind === "g") {
    if (u && u !== "g") return null;
  }
  out = kind === "stress" ? (out >= 10 ? Math.round(out) : Math.round(out * 10) / 10) : kind === "g" || kind === "ratio" ? Math.round(out * 1000) / 1000 : kind === "depth" ? Math.round(out * 100) / 100 : Math.round(out * 10) / 10;
  if (out < r.min || out > r.max) return null;
  return { value: out, conf, note };
}
const fmtN = (v: number, d = 3): string => String(Math.round(v * 10 ** d) / 10 ** d).replace(".", ",");

/** Adayın geldiği satırın tamamı (normalleştirilmiş) — deprem düzeyi (DD-2) tercihinde bakılır */
const CTX = new WeakMap<object, string>();

interface Hit { value: number; hi?: number; unit: string | null; direct: boolean; end: number }
/** Etiketten sonra gelen sayı: "= 18 kN/m³", "(kPa): 25", "28-30°", "değeri 0,85 olarak". Sonraki etikete kadar aranır. */
function valueAfter(n: string, from: number, to: number): Hit | null {
  const s = n.slice(from, to);
  const direct = new RegExp(String.raw`^(\)?\s*'?\s*(?:_?\s*\p{L}{1,4}\s*)?(?:\(([^)]{0,14})\)|\[([^\]]{0,14})\])?\s*(?:[=:≈≅~]|>=|<=|≥|≤|>|<)?\s*-?\s*)(${NUM})(\s*(?:-|÷|~|ile)\s*(${NUM}))?`, "u").exec(s);
  const loose = direct ? null : new RegExp(String.raw`^([^\d;]{0,40}?)(${NUM})(\s*(?:-|÷|~|ile)\s*(${NUM}))?`, "u").exec(s);
  const m = direct ?? loose;
  if (!m) return null;
  const numIdx = direct ? 4 : 2, hiIdx = direct ? 6 : 4;
  const after = s.slice(m[0].length);
  if (/^\s*%/.test(after) || /^[.,]\d/.test(after) || /^\s*(?:adet|sondaj|kuyu|nolu|no\b|yil|tabaka)/.test(after)) return null;
  // Koşul cümlesi ("φ = 0 olması durumunda") değer değildir
  if (/^\s*(?:\p{L}{0,4}\s*)?(?:olmasi|olursa|oldugunda|ise(?!\p{L})|durumunda|halinde|kabul\s*edilirse)/u.test(after)) return null;
  // Formül: "qt = 206,49 / 3 = 68,83 t/m²" → son "= sayı" sonuçtur
  if (/^\s*[*/×+]\s*[\d(]/.test(after)) {
    if (!/^\s*'?\s*(?:_?\s*\p{L}{1,4}\s*)?(?:\([^)]{0,14}\))?\s*=/u.test(s)) return null;   // "(qf/qlab) = …": etiket formülün içinde
    const res = [...s.matchAll(new RegExp(String.raw`=\s*(${NUM})(?!\s*[*/×+\d])`, "gu"))].pop();
    if (!res || res.index < m[0].length) return null;
    const tail = s.slice(res.index + res[0].length);
    const u = UNIT.exec(tail);
    return { value: toNum(res[1]!), unit: u?.[1] ?? null, direct: false, end: from + res.index + res[0].length + (u?.[0].length ?? 0) };
  }
  const inner = direct ? (m[2] ?? m[3] ?? "") : m[1] ?? "";
  if (!direct && /[.!?]\s|\b(?:ve|veya)\s*$/.test(inner)) return null;
  const um = UNIT.exec(after);
  const unitIn = UNIT_ANY.exec(inner)?.[1] ?? (/°|derece/.test(inner) ? "°" : null);
  const lo = toNum(m[numIdx]!), hi = m[hiIdx] ? toNum(m[hiIdx]!) : undefined;
  return { value: lo, hi: hi !== undefined && hi > lo ? hi : undefined, unit: um?.[1] ?? unitIn, direct: !!direct, end: from + m[0].length + (um?.[0].length ?? 0) };
}

interface Line { page: number; text: string; n: string; idx: number }
interface LabelHit { rule: Rule; start: number; end: number }

function labelsIn(n: string): LabelHit[] {
  const hits: LabelHit[] = [];
  for (const rule of RULES) {
    rule.label.lastIndex = 0;
    for (const m of n.matchAll(rule.label)) hits.push({ rule, start: m.index, end: m.index + m[0].length });
  }
  hits.sort((a, b) => a.start - b.start || b.end - a.end);
  // Çakışanlar: önce başlayan ve uzun olan kalır (ör. "doygun birim hacim ağırlığı" içindeki "birim hacim ağırlığı" atılır)
  const out: LabelHit[] = [];
  for (const h of hits) { const last = out[out.length - 1]; if (last && h.start < last.end) continue; out.push(h); }
  return out;
}

const snippetOf = (text: string, a = 0, b = text.length): string => { if (text.trim().length <= 140) return text.trim(); const s = text.slice(Math.max(0, a - 20), Math.min(text.length, b + 40)).trim(); return s.length > 160 ? `${s.slice(0, 157)}…` : s; };

/** Satırdaki etiket → değer adayları; "SS ve S1 sırasıyla 0,85 ve 0,24" biçimindeki liste de çözülür. */
function lineCandidates(line: Line, ctxBackfill: boolean): { key: ParamKey; f: Found }[] {
  const out: { key: ParamKey; f: Found }[] = [];
  const hits = labelsIn(line.n);
  const design = DESIGN.test(line.n);
  const backfill = ctxBackfill || BACKFILL.test(line.n);
  const push = (h: LabelHit, v: Hit, conf0: Conf, extra?: string): void => {
    const c = convert(h.rule.kind, v.value, v.unit, h.rule);
    if (!c) return;
    let conf: Conf = c.conf === "low" || conf0 === "low" ? "low" : c.conf === "medium" || conf0 === "medium" ? "medium" : "high";
    const notes = [c.note, extra];
    if (v.hi !== undefined) {
      const hiC = convert(h.rule.kind, v.hi, v.unit, { min: -Infinity, max: Infinity });
      notes.push(`aralık ${fmtN(c.value, 2)}–${fmtN(hiC?.value ?? v.hi, 2)}; güvenli tarafta alt değer alındı`);
      if (conf === "high") conf = "medium";
    }
    const f: Found = { value: c.value, page: line.page, snippet: snippetOf(line.text, h.start, v.end), confidence: conf, note: notes.filter(Boolean).join("; ") || undefined, design, backfill };
    CTX.set(f, line.n);
    out.push({ key: h.rule.key, f });
  };
  for (let i = 0; i < hits.length; i++) {
    const h = hits[i]!;
    const to = hits[i + 1]?.start ?? line.n.length;
    const v = valueAfter(line.n, h.end, to);
    if (v) { push(h, v, v.direct ? "high" : "medium"); continue; }
    // Liste: etiket₁ (ve|,|/) etiket₂ … sayı₁ (ve|,|/) sayı₂ …
    let j = i;
    while (j + 1 < hits.length && /^\s*(?:,|\/|ve|ile|;|-)?\s*$/.test(line.n.slice(hits[j]!.end, hits[j + 1]!.start).replace(/\([^)]{0,14}\)/g, ""))) j++;
    if (j === i) continue;
    const group = hits.slice(i, j + 1);
    if (new Set(group.map((g) => g.rule.key)).size !== group.length) continue;   // "İçsel sürtünme açısı φ" = tek etiket
    const rest = line.n.slice(group[group.length - 1]!.end, hits[j + 1]?.start ?? line.n.length);
    if (new RegExp(String.raw`${NUM}\s?(?:-|÷|~)\s?${NUM}`).test(rest)) continue;
    const nums = [...rest.matchAll(new RegExp(NUM, "g"))];
    if (nums.length >= group.length && /^[^\d]{0,60}$/.test(rest.slice(0, nums[0]!.index))) {
      group.forEach((g, k) => { const m = nums[k]!; const after = rest.slice(m.index + m[0].length); push(g, { value: toNum(m[0]), unit: UNIT.exec(after)?.[1] ?? null, direct: false, end: g.end + (m.index ?? 0) + m[0].length }, "medium", "liste biçiminden sırayla eşlendi"); });
      i = j;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------------
// Tabaka tabloları: başlık satırı (≥ 2 parametre sütunu) + altındaki sayısal satırlar
type Col = "depth" | "depthTop" | "depthBottom" | "gamma" | "gammaSat" | "gammaDry" | "phi" | "c" | "cu" | "spt";
const COLS: { col: Col; re: RegExp }[] = [
  { col: "depthTop", re: /ust\s*(?:derinlik|kot|sinir)|baslangic/ },
  { col: "depthBottom", re: /alt\s*(?:derinlik|kot|sinir)|bitis/ },
  { col: "depth", re: /derinlik|aralik\s*\(m\)|kot\s*\(m\)/ },
  { col: "spt", re: /spt|(?<!\p{L})n\s*_?\s*(?:30|60|ort)(?!\d)/u },
];
function headerCols(n: string): { col: Col; pos: number; unit: string | null }[] | null {
  const cols: { col: Col; pos: number; unit: string | null }[] = [];
  const taken: [number, number][] = [];
  for (const { col, re } of COLS) {
    const m = new RegExp(re.source, re.flags.includes("u") ? "gu" : "g").exec(n);
    if (m && !taken.some(([a, b]) => m.index < b && m.index + m[0].length > a)) { cols.push({ col, pos: m.index, unit: null }); taken.push([m.index, m.index + m[0].length]); }
  }
  for (const h of labelsIn(n)) {
    const k = h.rule.key;
    if (!["gamma", "gammaSat", "gammaDry", "phi", "c", "cu"].includes(k)) continue;
    if (taken.some(([a, b]) => h.start < b && h.end > a)) continue;
    if (cols.some((c) => c.col === k)) continue;
    const um = /^\s*[([]\s*([^)\]]{1,12})[)\]]/.exec(n.slice(h.end));
    cols.push({ col: k as Col, pos: h.start, unit: um ? (UNIT_ANY.exec(um[1]!)?.[1] ?? null) : null });
  }
  const params = cols.filter((c) => c.col !== "depth" && c.col !== "depthTop" && c.col !== "depthBottom" && c.col !== "spt");
  if (params.length < 2) return null;
  // Düz cümle başlık sanılmasın: tablo başlığı birim parantezi, geniş hücre boşluğu ya da ≥ 3 sütun taşır; fiil eki taşımaz
  if (/(?:mis|mektedir|maktadir|edil|olarak)\p{L}*/u.test(n) || /\.\s*$/.test(n)) return null;
  if (!(cols.length >= 3 || /[([]/.test(n) || /\S {2,}\S/.test(n))) return null;
  return cols.sort((a, b) => a.pos - b.pos);
}

type TabCol = Col | "name" | "no" | null;
const SOIL_KEYS = ["gamma", "gammaSat", "gammaDry", "phi", "c", "cu"];
/** Sekmeli tablo başlığı (Word tablosu, sekmeli PDF satırı): her hücre bir sütuna eşlenir; ≥ 2 zemin parametresi şart */
function tabHeader(text: string): { cols: TabCol[]; units: (string | null)[] } | null {
  if (!text.includes("\t")) return null;
  const cells = text.split("\t").map((c) => norm(c).trim());
  if (cells.length < 3 || cells.some((c) => new RegExp(`^-?${NUM}$`).test(c))) return null;
  const cols = cells.map((c): TabCol => {
    if (/^(?:katman|tabaka|birim|sira|seviye)\s*(?:no|numarasi|#)?\.?$/.test(c)) return "no";
    if (/^(?:zemin\s*(?:cinsi|turu|tanimi|tipi)|tanim\p{L}*|litoloji|aciklama|zemin|tabaka\s*(?:adi|cinsi|tanimi)|birim\s*(?:adi|tanimi))(?:\s*\(.*\))?$/u.test(c)) return "name";
    for (const { col, re } of COLS) if (new RegExp(re.source, re.flags.replace("g", "")).test(c)) return col;
    const h = labelsIn(c)[0];
    return h && h.start <= 2 && SOIL_KEYS.includes(h.rule.key) ? (h.rule.key as Col) : null;
  });
  if (cols.filter((c) => c && SOIL_KEYS.includes(c)).length < 2) return null;
  const units = cells.map((c) => UNIT_ANY.exec(/\(([^)]*)\)|\[([^\]]*)\]/.exec(c)?.slice(1).find(Boolean) ?? "")?.[1] ?? null);
  return { cols, units };
}

function parseTabTable(lines: Line[], i: number, hdr: { cols: TabCol[]; units: (string | null)[] }, used: Set<number>, warnings: string[]): LocalLayer[] {
  const out: LocalLayer[] = [];
  let miss = 0, prevBottom = 0;
  for (let j = i + 1; j < lines.length && miss < 3; j++) {
    const row = lines[j]!;
    if (tabHeader(row.text) || headerCols(row.n)) break;
    const cells = row.text.split("\t");
    if (cells.length < Math.max(3, hdr.cols.length - 1) || cells.length > hdr.cols.length + 1) { miss++; continue; }
    const L0: LocalLayer = { no: null, name: null, depthTop: null, depthBottom: null, gamma: null, gammaSat: null, phi: null, c: null, cu: null, sptN: null, page: row.page, snippet: row.text.replace(/\t/g, " · ").trim() };
    let dry: number | null = null;
    hdr.cols.forEach((col, k) => {
      const raw = (cells[k] ?? "").trim();
      if (!col || !raw) return;
      if (col === "name") { L0.name = raw.slice(0, 60); return; }
      const m = new RegExp(String.raw`^(-?${NUM})(?:\s*(?:-|÷|~)\s*(${NUM}))?`).exec(norm(raw));
      if (!m) return;
      const lo = toNum(m[1]!), hi = m[2] ? toNum(m[2]) : undefined;
      if (col === "no") { L0.no = lo; return; }
      if (col === "depth") { if (hi !== undefined) { L0.depthTop = lo; L0.depthBottom = hi; } else { L0.depthTop = prevBottom; L0.depthBottom = lo; } return; }
      if (col === "depthTop") { L0.depthTop = Math.abs(lo); return; }
      if (col === "depthBottom") { L0.depthBottom = Math.abs(lo); return; }
      if (col === "spt") { L0.sptN = lo; return; }
      const rule = RULES.find((r) => r.key === col)!;
      const conv = convert(rule.kind, lo, hdr.units[k] ?? null, rule.kind === "stress" ? { min: 0, max: rule.max } : rule);
      if (!conv) return;
      if (hi !== undefined) warnings.push(`${L0.name ?? "Tabaka"}: ${col} aralık (${fmtN(lo, 2)}–${fmtN(hi, 2)}) → alt değer alındı (s. ${row.page})`);
      if (col === "gammaDry") dry = conv.value; else (L0 as unknown as Record<string, number>)[col] = conv.value;
    });
    L0.gamma ??= dry;
    if ([L0.gamma, L0.gammaSat, L0.phi, L0.c, L0.cu].every((x) => x === null)) { miss++; continue; }
    miss = 0; used.add(j);
    if (L0.depthBottom !== null) prevBottom = L0.depthBottom;
    out.push(L0);
  }
  return out;
}

/** Aynı katmanın ayrı tablolardaki satırları (indeks özellikleri ↔ dayanım parametreleri) katman no + ad ile birleşir */
function mergeLayers(layers: LocalLayer[]): LocalLayer[] {
  const out: LocalLayer[] = [];
  const key = (l: LocalLayer): string => `${l.no ?? ""}|${norm(l.name ?? "").replace(/\s+/g, " ")}`;
  for (const l of layers) {
    const prev = l.no != null ? out.find((o) => o.no != null && key(o) === key(l) && Math.abs(o.page - l.page) <= 3 && (o.depthTop === null || l.depthTop === null || o.depthTop === l.depthTop)) : undefined;
    if (!prev) { out.push({ ...l }); continue; }
    for (const k of ["depthTop", "depthBottom", "gamma", "gammaSat", "phi", "c", "cu", "sptN"] as const) prev[k] ??= l[k];
    prev.snippet = `${prev.snippet} / ${l.snippet}`.slice(0, 300);
  }
  return out;
}

function parseTables(lines: Line[], warnings: string[]): { layers: LocalLayer[]; used: Set<number> } {
  const layers: LocalLayer[] = [], used = new Set<number>();
  for (let i = 0; i < lines.length; i++) {
    const tab = tabHeader(lines[i]!.text);
    if (tab) { used.add(i); layers.push(...parseTabTable(lines, i, tab, used, warnings)); continue; }
    const cols = headerCols(lines[i]!.n);
    if (!cols) continue;
    // Başlık sayı içermemeli (birim üstleri hariç) — aksi hâlde düz cümle
    if (new RegExp(`(?<![\\p{L}_])${NUM}`, "u").test(lines[i]!.n.replace(/\([^)]*\)|\[[^\]]*\]|n\s*_?\s*(?:30|60)/g, ""))) continue;
    used.add(i);
    // Başlık altındaki birim satırı (sayısız): gerilme birimi c/c_u sütunlarına
    let stressUnit: string | null = null;
    const unitLine = lines[i + 1];
    if (unitLine && !/\d(?![²³])/.test(unitLine.n.replace(/[²³]/g, ""))) {
      const u = unitLine.n.match(/(kpa|kn\/m²|t\/m²|ton\/m²|tf\/m²|kgf?\/cm²)/g);
      if (u && new Set(u).size === 1) stressUnit = u[0]!;
    }
    const hdrStress = cols.find((c) => (c.col === "c" || c.col === "cu") && c.unit)?.unit ?? stressUnit;
    let miss = 0, prevBottom = 0;
    for (let j = i + 1; j < lines.length && miss < 3; j++) {
      const row = lines[j]!;
      if (row.page !== lines[i]!.page && j > i + 1 && miss > 0) break;
      if (headerCols(row.n)) break;
      const toks = [...row.n.matchAll(new RegExp(String.raw`(${NUM}) ?- ?(${NUM})|(${NUM})|(?<=\s|^)(?:-|n\/a|yok)(?=\s|$)`, "gu"))];
      if (toks.length < 2) { miss++; continue; }
      let vals = toks.map((m) => m[1] !== undefined ? { lo: toNum(m[1]), hi: toNum(m[2]!) } : m[3] !== undefined ? { lo: toNum(m[3]), hi: undefined } : null);
      let nameFrom = 0, start = toks[0]!.index;
      if (vals.length === cols.length + 1 && vals[0] && vals[0].hi === undefined && Number.isInteger(vals[0].lo) && vals[0].lo < 30) { vals = vals.slice(1); nameFrom = toks[0]!.index + toks[0]![0].length; start = toks[1]!.index; }   // sıra no / birim no
      if (vals.length !== cols.length) { miss++; continue; }
      miss = 0; used.add(j);
      const name = row.text.slice(nameFrom, start).replace(/^[|:;,.\s-]+/, "").replace(/[|:;,\s-]+$/, "").trim() || null;
      const L0: LocalLayer = { name, depthTop: null, depthBottom: null, gamma: null, gammaSat: null, phi: null, c: null, cu: null, sptN: null, page: row.page, snippet: row.text.trim() };
      let dry: number | null = null;
      cols.forEach((c, k) => {
        const v = vals[k];
        if (!v) return;
        if (c.col === "depth") { if (v.hi !== undefined) { L0.depthTop = v.lo; L0.depthBottom = v.hi; } else { L0.depthTop = prevBottom; L0.depthBottom = v.lo; } return; }
        if (c.col === "depthTop") { L0.depthTop = v.lo; return; }
        if (c.col === "depthBottom") { L0.depthBottom = v.lo; return; }
        if (c.col === "spt") { L0.sptN = v.lo; return; }
        const rule = RULES.find((r) => r.key === c.col)!;
        const unit = c.unit ?? (c.col === "c" || c.col === "cu" ? hdrStress : null);
        const conv = convert(rule.kind, v.lo, unit, rule.kind === "stress" ? { min: 0, max: rule.max } : rule);
        if (!conv) return;
        if (v.hi !== undefined) warnings.push(`${name ?? "Tabaka"}: ${c.col} aralık (${fmtN(v.lo, 2)}–${fmtN(v.hi, 2)}) → alt değer alındı (s. ${row.page})`);
        if (c.col === "gammaDry") dry = conv.value; else (L0 as unknown as Record<string, number>)[c.col] = conv.value;
      });
      L0.gamma ??= dry;
      if ([L0.gamma, L0.gammaSat, L0.phi, L0.c, L0.cu].filter((x) => x !== null).length < 2) { miss++; used.delete(j); continue; }
      if (L0.depthBottom !== null) prevBottom = L0.depthBottom;
      layers.push(L0);
    }
  }
  return { layers: mergeLayers(layers), used };
}

// ---------------------------------------------------------------------------------------------------------------------
/** Aday listesinden tek değer: tasarım cümlesi öncelikli; tek farklı değer → güveni korunur; çok farklı değer → düşük (doldurulmaz) */
function resolve(key: ParamKey, list: Found[]): Found | undefined {
  if (!list.length) return undefined;
  let pool = list.some((f) => f.design) ? list.filter((f) => f.design) : list;
  if (["SS", "S1", "SDS", "SD1", "PGA"].includes(key)) {
    const ctx = (f: Found): string => CTX.get(f) ?? norm(f.snippet);
    const dd2 = pool.filter((f) => DD2.test(ctx(f)));
    const notOther = pool.filter((f) => !DD_OTHER.test(ctx(f)));
    pool = dd2.length ? dd2 : notOther.length ? notOther : pool;
  }
  const good = pool.filter((f) => f.confidence !== "low");
  if (good.length) pool = good;
  const distinct = [...new Set(pool.map((f) => f.value))].sort((a, b) => a - b);
  const best = pool.find((f) => f.confidence === "high") ?? pool[0]!;
  if (distinct.length === 1) return best;
  // Yuvarlama farkı (0,688 ↔ 0,689): hepsi ≤ %1,5 içindeyse tek değer
  if ((distinct[distinct.length - 1]! - distinct[0]!) <= 0.015 * Math.abs(distinct[distinct.length - 1]!)) return best;
  const lo = pool.find((f) => f.value === distinct[0])!;
  const list2 = distinct.slice(0, 5).map((v) => `${fmtN(v, 2)} (s. ${pool.find((f) => f.value === v)!.page})`).join(", ");
  return { ...lo, confidence: "low", note: `raporda farklı değerler: ${list2}` };
}

const title = (s: string): string => s.trim().replace(/\s+/g, " ").split(" ").map((w) => (w ? w[0]!.toLocaleUpperCase("tr") + w.slice(1).toLocaleLowerCase("tr") : w)).join(" ");

function siteInfo(lines: Line[]): LocalReport["site"] {
  const site: LocalReport["site"] = {};
  const set = (k: keyof LocalReport["site"], v: string, line: Line, a: number, b: number): void => {
    v = v.trim().replace(/[.,;:]+$/, "");
    if (!v || site[k]) return;
    site[k] = { value: v, page: line.page, snippet: snippetOf(line.text, a, b), confidence: "high" };
  };
  const W = String.raw`([\p{L}][\p{L}]{1,24})`;
  const STOP = /^(?:ve|ile|bu|olan|icin|raporu|merkez|zemin|proje|parsel|ada|pafta|mahalle\w*|koy\w*|belde\w*|sinir\w*)$/;
  for (const line of lines) {
    const n = line.n, o = line.text;
    const orig = (m: RegExpExecArray, g: number): string => { const st = m.index + m[0].indexOf(m[g]!); return o.slice(st, st + m[g]!.length); };
    let m: RegExpExecArray | null;
    if ((m = new RegExp(String.raw`(?<!\p{L})il\s*/\s*ilce\s*[:：]?\s*${W}\s*/\s*${W}`, "u").exec(n))) { set("city", title(orig(m, 1)), line, m.index, m.index + m[0].length); set("district", title(orig(m, 2)), line, m.index, m.index + m[0].length); }
    if ((m = new RegExp(String.raw`(?<!\p{L})il(?:i)?\s*[:：]\s*${W}`, "u").exec(n)) && !STOP.test(m[1]!)) set("city", title(orig(m, 1)), line, m.index, m.index + m[0].length);
    if ((m = new RegExp(String.raw`${W}\s+ili(?!\p{L})`, "u").exec(n)) && !STOP.test(m[1]!) && m[1] !== "ilgili") set("city", title(orig(m, 1)), line, m.index, m.index + m[0].length);
    if ((m = new RegExp(String.raw`(?<!\p{L})ilce(?:si)?\s*[:：]\s*${W}`, "u").exec(n)) && !STOP.test(m[1]!)) set("district", title(orig(m, 1)), line, m.index, m.index + m[0].length);
    if ((m = new RegExp(String.raw`${W}\s+ilcesi(?!\p{L})`, "u").exec(n)) && !STOP.test(m[1]!)) set("district", title(orig(m, 1)), line, m.index, m.index + m[0].length);
    if ((m = /(?<!\p{L})ada\s*\/\s*parsel\s*(?:no\s*)?[:：]?\s*(\d{1,6})\s*\/\s*(\d{1,6}(?:\s*[,-]\s*\d{1,6})*)/u.exec(n))) { set("block", m[1]!, line, m.index, m.index + m[0].length); set("parcel", m[2]!.replace(/\s+/g, ""), line, m.index, m.index + m[0].length); }
    // "1234 ada 5 parsel" ve "ada 1234 parsel 5": ada önce sayı-önde, parsel önce etiket-önde aranır
    if ((m = /(?<![\p{L}\d])(\d{1,6})\s*(?:no\s*'?\s*lu\s*|numarali\s*|nolu\s*)?ada(?!\p{L})/u.exec(n)) || (m = /(?<!\p{L})ada\s*(?:no\s*)?[:：]?\s*(\d{1,6})(?!\d)/u.exec(n))) set("block", m[1]!, line, m.index, m.index + m[0].length);
    if ((m = /(?<!\p{L})parsel\s*(?:no\s*)?[:：]?\s*(\d{1,6}(?:\s*[,-]\s*\d{1,6})*)/u.exec(n)) || (m = /(?<![\p{L}\d])(\d{1,6}(?:\s*[,-]\s*\d{1,6})*)\s*(?:no\s*'?\s*lu\s*|numarali\s*|nolu\s*)?parsel/u.exec(n))) set("parcel", m[1]!.replace(/\s+/g, ""), line, m.index, m.index + m[0].length);
    if ((m = /(?<!\p{L})pafta\s*(?:no\s*)?[:：]\s*([\p{L}\d][\p{L}\d\-./]{0,18})/u.exec(n)) || (m = /(?<![\p{L}\d])([\p{L}]?\d{1,3}[\p{L}\d\-./]{1,16})\s+(?:nolu\s*)?pafta(?!\p{L})/u.exec(n))) set("sheet", orig(m, 1).toUpperCase(), line, m.index, m.index + m[0].length);
  }
  return site;
}

function siteClassOf(lines: Line[]): Found<string> | undefined {
  const list: Found<string>[] = [];
  for (const line of lines) {
    const lm = /(?:yerel\s*)?zemin\s*sinif/.exec(line.n);
    if (!lm) continue;
    const tail = line.n;
    const all = [...tail.matchAll(/(?<![\p{L}\d])z\s?([a-f])(?![\p{L}\d])/gu)];
    if (!all.length) continue;
    const uniq = [...new Set(all.map((m) => m[1]!))];
    const m = all[0]!;
    list.push({ value: `Z${m[1]!.toUpperCase()}`, page: line.page, snippet: snippetOf(line.text, Math.min(lm.index, m.index ?? 0), Math.max(lm.index + lm[0].length, (m.index ?? 0) + 4)), confidence: uniq.length > 1 ? "low" : "high", design: DESIGN.test(line.n) || /olarak|sahip|sinifinda|belirlen|siniflandir/.test(line.n) });
  }
  if (!list.length) return undefined;
  const pool = list.some((f) => f.design && f.confidence !== "low") ? list.filter((f) => f.design && f.confidence !== "low") : list.filter((f) => f.confidence !== "low").length ? list.filter((f) => f.confidence !== "low") : list;
  const vals = [...new Set(pool.map((f) => f.value))];
  if (vals.length === 1) return pool[0];
  // Açık çoğunluk (ör. 4 × ZC, 1 × ZF atfı) → çoğunluk, not düşülür
  const all = list.filter((f) => f.confidence !== "low");
  const count = (v: string): number => all.filter((f) => f.value === v).length;
  const [top, second] = [...vals].sort((a, b) => count(b) - count(a));
  if (count(top!) >= 2 * count(second!)) { const f = pool.find((x) => x.value === top) ?? all.find((x) => x.value === top)!; return { ...f, confidence: "high", note: `raporda ${count(top!)} yerde ${top}; ${vals.filter((v) => v !== top).join(", ")} yalnız atıf olarak geçiyor` }; }
  return { ...pool[0]!, confidence: "low", note: `raporda farklı sınıflar: ${vals.join(", ")}` };
}

function groundwaterOf(lines: Line[]): Found<number | null> | undefined {
  const vals: Found[] = []; let none: Found<null> | undefined; let zero: Found | undefined;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const lm = GW_LABEL.exec(line.n);
    if (!lm) continue;
    const next = lines[i + 1]?.page === line.page ? lines[i + 1]!.n : "";
    const tail = line.n.slice(lm.index);
    if (GW_RULE.test(line.n)) continue;
    // Tablo hücresi: "Yer Altı Su Seviyesi (m) | Yok", "YASS (m) Yok"
    const noneCell = /^\s*(?:\p{L}*\s*)?(?:\([^)]{0,6}\)\s*)?[:=]?\s*yok(?!\p{L})/u.test(tail.slice(lm[0].length));
    if (noneCell || GW_NONE.test(tail) || (!/\d/.test(tail) && GW_NONE.test(next))) { none ??= { value: null, page: line.page, snippet: snippetOf(line.text, lm.index), confidence: "high" }; continue; }
    // "… 3,50 m derinlikte / m'de" ya da "YASS = -3,50 m"; sondaj derinliği ("30 m'lik sondaj") dışarıda
    const m = new RegExp(String.raw`(?:[=:]\s*)?-?\s*(${NUM})(?:\s*(?:-|ile)\s*(${NUM}))?\s*(?:m(?![\p{L}²³])|metre)`, "u").exec(tail);
    if (!m) continue;
    const after = tail.slice(m.index + m[0].length);
    if (/^\s*(?:'?\s*lik|derinli[kg]\w*\s*sondaj|sondaj)/.test(after)) continue;
    const v = toNum(m[1]!);
    if (v < 0 || v > 60) continue;
    const found: Found<number> = { value: v, page: line.page, snippet: snippetOf(line.text, lm.index, lm.index + m.index + m[0].length), confidence: "high", note: m[2] ? `aralık ${m[1]}–${m[2]}; en sığ alındı` : undefined };
    // 0 m ("YASS 0,00" ya da "0–30 m" aralık başı) çoğunlukla ölçüm değil; dolguyu tamamen doygun sayar → forma yazılmaz, ipucu olur
    if (v === 0) { zero ??= { ...found, confidence: "low", note: "0 m okundu (yüzeyde) — kontrol edin" }; continue; }
    vals.push(found);
  }
  if (vals.length) {
    const distinct = [...new Set(vals.map((f) => f.value))].sort((a, b) => a - b);
    const lo = vals.find((f) => f.value === distinct[0])!;
    return distinct.length === 1 ? lo : { ...lo, confidence: "medium", note: `farklı sondajlarda ${distinct.map((d) => fmtN(d, 2)).join(" / ")} m; en sığ (güvenli) alındı` };
  }
  return none ?? zero;
}

const BUILDING_MARKS = [/radye/, /(?<!\p{L})bina(?!\p{L})|binalar/u, /yapi\s*agirlig/, /bina\s*kullanim\s*sinif|(?<!\p{L})bks(?!\p{L})/u, /bina\s*yukseklik\s*sinif|(?<!\p{L})bys(?!\p{L})/u, /kat\s*(?:adedi|sayisi)|bodrum\s*kat/, /temel\s*sistemi/, /(?<!\p{L})[a-z](?:\s*-\s*[a-z])?\s*blok(?!\p{L})/u];
const RETAINING = /istinat|dayanma\s*(?:yapi|duvar)|agirlik\s*duvar|kargir\s*duvar|konsol\s*duvar/;
/** Rapor bir bina etüdü mü? ≥ 3 farklı bina işareti ve istinat/dayanma yapısı hiç geçmiyor */
function buildingOf(lines: Line[]): LocalReport["building"] {
  if (lines.some((l) => RETAINING.test(l.n))) return undefined;
  const hits = BUILDING_MARKS.map((re) => lines.find((l) => re.test(l.n))).filter((l): l is Line => !!l);
  if (hits.length < 3) return undefined;
  const first = hits.sort((a, b) => a.idx - b.idx)[0]!;
  return { page: first.page, snippet: snippetOf(first.text) };
}

/** Ana giriş: sayfa satırlarından rapor özeti. */
export function parseSoilReport(pages: PageLines[], opts: { pageCount?: number; scanned?: boolean } = {}): LocalReport {
  const lines: Line[] = [];
  for (const p of pages) for (const text of p.lines) lines.push({ page: p.page, text, n: norm(text), idx: lines.length });
  const warnings: string[] = [];
  const { layers, used } = parseTables(lines, warnings);
  const candidates: Partial<Record<ParamKey, Found[]>> = {};
  const backfillC: Partial<Record<"gamma" | "phi" | "c", Found[]>> = {};
  let bfCtx = 0;
  for (const line of lines) {
    if (used.has(line.idx)) continue;
    // Geri dolgu başlığından sonraki 3 satır dolgu bağlamında sayılır
    if (BACKFILL.test(line.n)) bfCtx = 4;
    else if (/temel\s*zemin|tabaka|birim\s*-?\s*\d|zemin\s*profil|sondaj/.test(line.n)) bfCtx = 0;
    const cands = lineCandidates(line, bfCtx > 0);
    if (bfCtx > 0) bfCtx--;
    for (const { key, f } of cands) {
      if (f.backfill && (key === "gamma" || key === "phi" || key === "c")) (backfillC[key] ??= []).push(f);
      else (candidates[key] ??= []).push(f);
    }
    // Metin içi tabaka: aynı satırda ≥ 2 zemin parametresi + derinlik aralığı → tabaka
    const soil = cands.filter((c) => !c.f.backfill && !c.f.design && ["gamma", "phi", "c", "cu"].includes(c.key));
    if (new Set(soil.map((c) => c.key)).size >= 2) {
      const dm = new RegExp(String.raw`(${NUM})\s*-\s*(${NUM})\s*m(?![\p{L}²³])`, "u").exec(line.n);
      if (dm) {
        const L0: LocalLayer = { name: line.text.slice(0, dm.index).replace(/[(:\-–\s]+$/, "").trim() || null, depthTop: toNum(dm[1]!), depthBottom: toNum(dm[2]!), gamma: null, gammaSat: null, phi: null, c: null, cu: null, sptN: null, page: line.page, snippet: line.text.trim() };
        for (const c of soil) (L0 as unknown as Record<string, number>)[c.key] ??= c.f.value;
        if (L0.name && L0.name.length > 60) L0.name = L0.name.slice(0, 60);
        layers.push(L0);
      }
    }
  }
  const values: LocalReport["values"] = {};
  for (const k of Object.keys(candidates) as ParamKey[]) { const r = resolve(k, candidates[k]!); if (r) values[k] = r; }
  const backfill: LocalReport["backfill"] = {};
  for (const k of Object.keys(backfillC) as ("gamma" | "phi" | "c")[]) { const r = resolve(k, backfillC[k]!); if (r) backfill[k] = r; }
  // Doğal γ yoksa kuru γ ipucu
  if (!values.gamma && values.gammaDry) values.gamma = { ...values.gammaDry, confidence: "low", note: `yalnız kuru birim hacim ağırlığı bulundu${values.gammaDry.note ? `; ${values.gammaDry.note}` : ""}` };
  for (const k of Object.keys(values) as ParamKey[]) { const f = values[k]!; if (f.confidence === "low") warnings.push(`${k}: ${f.note ?? "belirsiz"} — doldurulmadı`); }
  const first = lines.filter((l) => l.page === (pages[0]?.page ?? 1));
  const titleLine = first.find((l) => /zemin\s*(?:ve\s*temel\s*)?etu[dt]|[gj]eoteknik\s*(?:etut|rapor|degerlendirme|inceleme)/.test(l.n));
  const dateM = lines.slice(0, 80).map((l) => /(?<!\d)(\d{1,2}[./]\d{1,2}[./](?:19|20)\d{2})(?!\d)/.exec(l.text)?.[1]).find(Boolean) ?? null;
  return {
    pageCount: opts.pageCount ?? pages.length, scanned: !!opts.scanned,
    title: titleLine ? titleLine.text.trim().slice(0, 120) : null, date: dateM,
    site: siteInfo(lines), siteClass: siteClassOf(lines), groundwater: groundwaterOf(lines),
    values, backfill, candidates, layers, warnings, building: buildingOf(lines),
  };
}

// ---------------------------------------------------------------------------------------------------------------------
const ok = <T,>(f: Found<T> | undefined): f is Found<T> => !!f && f.confidence !== "low";
const val = (f: Found | undefined): number | null => (ok(f) ? f.value : null);

/** Temel tabakası: önerilen temel derinliğinin altına uzanan ilk tabaka; yoksa -1 (belirsiz) */
export function foundationLayerIndex(r: LocalReport): number {
  if (r.layers.length === 1) return 0;
  const df = val(r.values.df);
  if (df === null) return -1;
  return r.layers.findIndex((l) => l.depthBottom !== null && l.depthBottom > df + 1e-6);
}

export interface SoilPick { gamma?: Found; phi?: Found; c?: Found; undrained?: boolean; /** rapordaki drenajsız kayma dayanımı (c ile birlikte verilse de) */ cu?: Found; layer: number }
/**
 * Temel zemini değerleri: rapordaki tasarım cümlesi (hesaplarda … alınmıştır) → seçilen tabaka → tek genel değer.
 * Tabaka seçimi belirsizse (çok tabaka, Df yok) değerler doldurulmaz; `layer = -1` döner, kullanıcı tabakayı seçer.
 */
export function pickFoundationSoil(r: LocalReport, layerIdx?: number): SoilPick {
  const v = r.values;
  const designOf = (k: ParamKey): Found | undefined => (v[k]?.design && ok(v[k]) ? v[k] : undefined);
  const li = layerIdx ?? foundationLayerIndex(r);
  const layer = li >= 0 ? r.layers[li] : undefined;
  const fromLayer = (x: number | null, what: string): Found | undefined => (layer && x !== null ? { value: x, page: layer.page, snippet: layer.snippet, confidence: layerIdx !== undefined || r.layers.length === 1 ? "high" : "medium", note: `${layer.name ?? "tabaka"}${layer.depthTop !== null && layer.depthBottom !== null ? ` (${fmtN(layer.depthTop, 2)}–${fmtN(layer.depthBottom, 2)} m)` : ""} — ${what}` } : undefined);
  const multi = r.layers.length > 1 && li < 0;
  const gen = (k: ParamKey): Found | undefined => (multi ? undefined : ok(v[k]) ? v[k] : undefined);
  const gamma = designOf("gamma") ?? fromLayer(layer?.gamma ?? null, "γ") ?? gen("gamma");
  const phi = designOf("phi") ?? fromLayer(layer?.phi ?? null, "φ") ?? gen("phi");
  let c = designOf("c") ?? fromLayer(layer?.c ?? null, "c") ?? gen("c");
  let undrained = false;
  const cu = designOf("cu") ?? fromLayer(layer?.cu ?? null, "c_u") ?? gen("cu");
  if (!c && cu && (!phi || phi.value < 1)) { c = { ...cu, note: `${cu.note ? `${cu.note}; ` : ""}drenajsız (c_u)` }; undrained = true; }
  return { gamma, phi: phi ?? (undrained ? { ...cu!, value: 0, note: "drenajsız: φ = 0" } : undefined), c, undrained, cu, layer: li };
}

/** Yerel okuma → yapay zekâ çıktısıyla aynı şekil (SoilReportDialog öneri tablosu ortak). Düşük güvenli değerler null. */
export function toSoilExtraction(r: LocalReport): SoilExtraction {
  const conf = (...f: (Found<unknown> | undefined)[]): "high" | "medium" | null => { const g = f.filter(ok); return !g.length ? null : g.every((x) => x.confidence === "high") ? "high" : "medium"; };
  const page = (...f: (Found<unknown> | undefined)[]): number | null => f.find(ok)?.page ?? null;
  let layers: ExtractedLayer[] = r.layers.map((l) => ({ name: l.name, description: null, depthTop: l.depthTop, depthBottom: l.depthBottom, gamma: l.gamma, gammaSat: l.gammaSat, phi: l.phi, c: l.c, cu: l.cu, sptN: l.sptN, page: l.page }));
  const v = r.values;
  // Tasarım cümlesi ya da tabakasız genel değerler → "rapor genel değerleri" tabakası (öneri tablosu temel zeminini tabakadan okur)
  const d = (k: ParamKey): number | null => (v[k]?.design || !r.layers.length ? val(v[k]) : null);
  if (d("gamma") !== null || d("phi") !== null || d("c") !== null || d("cu") !== null) {
    layers = [{ name: "Rapor tasarım değerleri", description: null, depthTop: null, depthBottom: null, gamma: d("gamma"), gammaSat: val(v.gammaSat), phi: d("phi"), c: d("c"), cu: d("cu"), sptN: null, page: page(v.gamma, v.phi, v.c, v.cu) }, ...layers];
  }
  const li = foundationLayerIndex(r);
  const warnings = [...r.warnings];
  if (r.layers.length > 1 && li < 0) warnings.push("Temel derinliği bulunamadı; temel zemini tabakasını listeden seçin.");
  if (r.building && (ok(v.df) || ok(v.qa) || ok(v.qult))) warnings.push(`Temel derinliği ve taşıma gücü: ${BUILDING_WARN.charAt(0).toLocaleLowerCase("tr")}${BUILDING_WARN.slice(1)} (s. ${r.building.page}).`);
  const b = r.backfill;
  return {
    document: { title: r.title, firm: null, date: r.date, pageCount: r.pageCount, language: "tr" },
    site: { city: r.site.city?.value ?? null, district: r.site.district?.value ?? null, block: r.site.block?.value ?? null, parcel: r.site.parcel?.value ?? null },
    layers: layers.slice(0, 30),
    groundwater: { depth: ok(r.groundwater) ? r.groundwater.value : null, note: r.groundwater && r.groundwater.value === null ? "Yeraltı suyuna rastlanmamış" : r.groundwater?.note ?? null, page: r.groundwater?.page ?? null, confidence: r.groundwater ? r.groundwater.confidence : null },
    foundation: {
      recommendedDepth: val(v.df), bearingLayerName: li >= 0 && layers.length ? (r.layers[li]?.name ?? null) : null,
      allowableBearing: val(v.qa), ultimateBearing: val(v.qult), baseFrictionAngle: val(v.delta), frictionCoefficient: val(v.mu),
      note: v.qa?.note ?? null, page: page(v.qa, v.qult, v.df), confidence: conf(v.qa, v.qult),
    },
    seismic: { siteClass: ok(r.siteClass) ? r.siteClass.value : null, SS: val(v.SS), S1: val(v.S1), SDS: val(v.SDS), SD1: val(v.SD1), PGA: val(v.PGA), designLevel: null, page: page(v.SS, v.S1, v.SDS, r.siteClass), confidence: conf(v.SS, v.S1, v.SDS, r.siteClass) },
    backfill: { gamma: val(b.gamma), phi: val(b.phi), c: val(b.c), note: null, page: page(b.gamma, b.phi, b.c), confidence: conf(b.gamma, b.phi, b.c) },
    warnings: warnings.slice(0, 20).map((w) => w.slice(0, 500)),
  };
}
